/*!
 * Copyright (c) REWE Digital GmbH
 */
import { Host, h } from "@stencil/core";
import { gotoPage, replaceTranslation } from "@rdss/core";
const DefaultSnippets = {
    labelAriaCartOverview: 'Warenkorbübersicht',
    shippingCostsReached: 'Versandkostenfreie Lieferung',
    totalCartValue: 'Gesamtsumme inkl. MwSt.',
    labelAriaCartSummary: 'Warenkorbwert und Differenz von Bestellwert für kostenfreie Lieferung',
    toCart: 'Zum Warenkorb',
    addToCart: 'Zum Warenkorb hinzugefügt',
    labelAriaOrderValue: 'Differenz von Bestellwert für kostenfreie Lieferung',
    labelAriaPresentCartValue: 'Aktueller Warenkorbwert',
};
/**
 * This component represents shopping cart summary data in an overlay.
 * The behavior is optimized for display on different devices.
 *
 * @example
 *
 * <vx-cart-summary cartData={cartData}/>
 *
 * @part container - The whole container.
 * @part wrapper - The wrapper for the cart content.
 */
// eslint-disable-next-line require-jsdoc
export class VxCartSummary {
    constructor() {
        /**
         * This helper function makes the browser jump to the shopping cart page.
         */
        this.gotoCartPage = () => {
            if (location.pathname.includes(this.cartUrl))
                return;
            gotoPage(this.cartUrl);
        };
        this.snippets = DefaultSnippets;
        this.cartUrl = '/checkout/cart';
        this.displayTime = 5000;
        this.cartData = undefined;
        this.newItemVibration = true;
        this.vibrationDuration = 200;
        this.showNoCartItemsMessage = false;
        this.cartIsDisplayed = false;
        this.cartHasNewItem = false;
    }
    /**
     * Processes the changes in the shopping cart data to
     * switch the display of the shopping cart.
     */
    handleCartChange(newVal, oldVal) {
        if (oldVal && newVal.cartItemsCount > (oldVal === null || oldVal === void 0 ? void 0 : oldVal.cartItemsCount)) {
            this.cartHasNewItem = true;
            this.showCartHasNewItems();
        }
    }
    /**
     * Watches for snippet changes and merges the default values with the
     * new prop values.
     */
    setSnippets(newVal, oldVal) {
        if (JSON.stringify(oldVal) !== JSON.stringify(newVal)) {
            this.snippets = Object.assign(Object.assign({}, this.snippets), newVal);
        }
    }
    /**
     * @inheritDoc
     */
    render() {
        var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p;
        return (h(Host, { key: '7ce3fae20bac0cd991fe410b8630d05e96deefb3' }, h("div", { key: '7b6ec40d8b8221d92a7ba02e9cb395e588e41694', part: "container", "aria-label": this.snippets.labelAriaCartOverview, "aria-hidden": !this.cartIsDisplayed, class: {
                'cart-overlay-container': true,
                'has-new-item': this.cartHasNewItem,
            } }, h("div", { key: '1386433c9bd1fce1095a08792740cda4393f7a15', part: "wrapper", class: { wrapper: true, 'has-new-item': this.cartHasNewItem } }, h("div", { key: '90b287410523af39653142a430d4e4ab1093dbe9', class: "new-item-notification" }, h("div", { key: '6a71a2d9066f44ef6c469423cb2f48aa64303be8', class: "new-item-notification-content notification-color" }, h("vx-icon", { key: '70f5e1512374df0ed0a70e36964e67e74cb6d842', icon: "check_circle", customClass: "new-item-icon reinvert" }), h("span", { key: 'c10a6564f9dcf8f293e6c9e031d9f94d6bcbe67d', class: "new-item-text" }, this.snippets.addToCart))), h("div", { key: '761cce4eedd978bad8777f169a3003c56e6e90bc', class: "summary", "aria-label": this.snippets.labelAriaCartSummary }, h("span", { key: 'a0a5ee95f4e3c21224939dbd5380ed370a6f0ba9', class: "gross-amount-text" }, this.snippets.totalCartValue), h("span", { key: '367e7ca0bbb0de4b0cf51b447dec825644bdb5f1', class: "spacer" }), h("span", { key: '034a79da6ffbbae415076d35d92ca8045d1f1960', class: "gross-amount-value", "aria-label": this.snippets.labelAriaPresentCartValue }, (_c = (_b = (_a = this.cartData) === null || _a === void 0 ? void 0 : _a.summary) === null || _b === void 0 ? void 0 : _b.total) !== null && _c !== void 0 ? _c : '0')), h("div", { key: 'b13ee94ae3e96541aaec8d4e231e85afd8a4e622', class: {
                'shipping-costs-difference': true,
                blink: !!this.cartIsDisplayed,
            } }, h("span", { key: '8159b2d404250c8d9dfa584e3f22f64eb9e0f33b', class: { 'cart-green': (_f = (_e = (_d = this.cartData) === null || _d === void 0 ? void 0 : _d.shipping) === null || _e === void 0 ? void 0 : _e.costs) === null || _f === void 0 ? void 0 : _f.free }, "aria-label": this.snippets.labelAriaOrderValue }, replaceTranslation((_j = (_h = (_g = this.cartData) === null || _g === void 0 ? void 0 : _g.shipping) === null || _h === void 0 ? void 0 : _h.costs) === null || _j === void 0 ? void 0 : _j.text, '%shippingCostDifference', (_m = (_l = (_k = this.cartData) === null || _k === void 0 ? void 0 : _k.shipping) === null || _l === void 0 ? void 0 : _l.costs) === null || _m === void 0 ? void 0 : _m.difference))), !location.pathname.includes(this.cartUrl) && ((_o = this.cartData) === null || _o === void 0 ? void 0 : _o.cartItemsCount) > 0 &&
            h("vx-button", { "aria-label": this.snippets.labelAriaToCart, onClick: this.gotoCartPage, fullwidth: true, label: this.snippets.toCart, raised: true }), this.showNoCartItemsMessage && ((_p = this.cartData) === null || _p === void 0 ? void 0 : _p.cartItemsCount) === 0 &&
            h("p", null, this.snippets.noItemInCart)))));
    }
    /**
     * Shows the overlay.
     */
    showCartHasNewItems() {
        if (navigator.vibrate) {
            navigator.vibrate(this.vibrationDuration);
        }
        clearTimeout(this.timer);
        this.timer = window.setTimeout(() => {
            this.cartHasNewItem = false;
        }, this.displayTime);
    }
    static get is() { return "vx-cart-summary"; }
    static get encapsulation() { return "scoped"; }
    static get originalStyleUrls() {
        return {
            "$": ["vx-cart-summary.scss"]
        };
    }
    static get styleUrls() {
        return {
            "$": ["vx-cart-summary.css"]
        };
    }
    static get properties() {
        return {
            "snippets": {
                "type": "unknown",
                "mutable": true,
                "complexType": {
                    "original": "Snippets",
                    "resolved": "Snippets",
                    "references": {
                        "Snippets": {
                            "location": "import",
                            "path": "./types",
                            "id": "src/components/vx-cart-summary/types/index.ts::Snippets"
                        }
                    }
                },
                "required": false,
                "optional": true,
                "docs": {
                    "tags": [],
                    "text": "The snippets for elements labels, description and so on.\nHere you can pass your translations."
                },
                "defaultValue": "DefaultSnippets"
            },
            "cartUrl": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [{
                            "name": "default",
                            "text": "'/checkout/cart'."
                        }],
                    "text": "Path/URL of the cart page."
                },
                "attribute": "cart-url",
                "reflect": false,
                "defaultValue": "'/checkout/cart'"
            },
            "displayTime": {
                "type": "number",
                "mutable": false,
                "complexType": {
                    "original": "number",
                    "resolved": "number",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [{
                            "name": "default",
                            "text": "4000ms."
                        }],
                    "text": "The time it takes to close the new item information."
                },
                "attribute": "display-time",
                "reflect": false,
                "defaultValue": "5000"
            },
            "cartData": {
                "type": "unknown",
                "mutable": true,
                "complexType": {
                    "original": "CartData",
                    "resolved": "CartData",
                    "references": {
                        "CartData": {
                            "location": "import",
                            "path": "./types",
                            "id": "src/components/vx-cart-summary/types/index.ts::CartData"
                        }
                    }
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "The cart Data."
                }
            },
            "newItemVibration": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "If true, the user will receive physical feedback when\nthe shopping cart has received a new item."
                },
                "attribute": "new-item-vibration",
                "reflect": false,
                "defaultValue": "true"
            },
            "vibrationDuration": {
                "type": "number",
                "mutable": false,
                "complexType": {
                    "original": "number",
                    "resolved": "number",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [{
                            "name": "default",
                            "text": "200"
                        }],
                    "text": "The vibration duration in ms."
                },
                "attribute": "vibration-duration",
                "reflect": false,
                "defaultValue": "200"
            },
            "showNoCartItemsMessage": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Shows a message if cart is empty."
                },
                "attribute": "show-no-cart-items-message",
                "reflect": false,
                "defaultValue": "false"
            }
        };
    }
    static get states() {
        return {
            "cartIsDisplayed": {},
            "cartHasNewItem": {}
        };
    }
    static get watchers() {
        return [{
                "propName": "cartData",
                "methodName": "handleCartChange"
            }, {
                "propName": "snippets",
                "methodName": "setSnippets"
            }];
    }
}
//# sourceMappingURL=vx-cart-summary.js.map
